import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  LogOut,
  KeyRound,
  Bell,
  ChevronDown,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { useBadgesMercado } from "@/hooks/use-mercado";
import { itensDeAdministracao } from "@/lib/menu-administracao";
import { TrocarSenhaDialog } from "@/components/admin/trocar-senha-dialog";
import {
  getCurrentModule,
  visibleModules,
  itensDoPublico,
  rotaDefineModulo,
  taglineDoModulo,
  LAST_MODULE_KEY,
  type RizomaModule,
  type ModuleId,
} from "@/lib/modules";

// Paths públicos onde não faz sentido persistir o módulo
const PUBLIC_PATHS = ["/", "/login", "/signup"];

function RizomaLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Rizoma"
    >
      <rect width="44" height="44" rx="9" fill="#2D5016" />
      <circle cx="14" cy="22" r="4.5" fill="white" />
      <circle cx="27" cy="14" r="3.5" fill="white" opacity="0.75" />
      <circle cx="27" cy="30" r="3.5" fill="white" opacity="0.75" />
      <circle cx="37" cy="22" r="2.5" fill="#C17BC0" opacity="0.9" />
      <line x1="14" y1="22" x2="24" y2="15" stroke="white" strokeWidth="1.4" opacity="0.5" />
      <line x1="14" y1="22" x2="24" y2="29" stroke="white" strokeWidth="1.4" opacity="0.5" />
      <line x1="27" y1="14" x2="35" y2="22" stroke="#C17BC0" strokeWidth="1.2" opacity="0.5" />
      <line x1="27" y1="30" x2="35" y2="22" stroke="#C17BC0" strokeWidth="1.2" opacity="0.5" />
    </svg>
  );
}

function ModuleSwitcher({
  current,
  modules,
  onSelect,
  orgTipo,
}: {
  current: RizomaModule;
  modules: RizomaModule[];
  onSelect: (mod: RizomaModule) => void;
  /** Só o Mercado usa: os dois lados veem o mesmo módulo, e não a mesma coisa. */
  orgTipo?: "PRODUTOR" | "COMPRADOR";
}) {
  const others = modules.filter((m) => m.id !== current.id);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-between h-auto py-2 px-3"
        >
          <div className="flex items-center gap-2 min-w-0">
            <current.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="text-left min-w-0">
              <p className="text-xs font-semibold leading-none">{current.label}</p>
              <p className="text-[10px] text-muted-foreground leading-snug truncate">
                {taglineDoModulo(current, orgTipo)}
              </p>
            </div>
          </div>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        {others.map((mod) => (
          <DropdownMenuItem
            key={mod.id}
            onClick={() => onSelect(mod)}
            className="gap-2 cursor-pointer"
          >
            <mod.icon className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium leading-none">{mod.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{taglineDoModulo(mod, orgTipo)}</p>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppSidebar() {
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const { user, isAdmin, isPlataforma, modulesEnabled, logout } = useAuth();
  const { setOpenMobile } = useSidebar();

  // O módulo selecionado é ESTADO, não derivação pura da rota: telas de
  // Administração (Conectores, Usuários, Variedades...) são neutras e mantêm o
  // módulo que já estava. Antes, abrir Conectores estando no Manejo jogava o
  // usuário no Cacho, porque a rota não batia com nenhum prefixo de módulo.
  const [moduloSelecionado, setModuloSelecionado] = useState<ModuleId | null>(() => {
    try {
      return (localStorage.getItem(LAST_MODULE_KEY) as ModuleId | null) ?? null;
    } catch {
      return null; // localStorage indisponível (modo privado/SSR) — cai no padrão
    }
  });

  // A Plataforma entra pela flag do usuário, não por `modules_enabled`.
  const visibleMods = visibleModules(modulesEnabled, isPlataforma);
  // ⚠️ `visibleMods` ENTRA na resolução. Sem isso, numa rota de administração sem módulo
  // previamente selecionado o fallback era o Cacho — e o corretor via o menu inteiro do
  // Cacho em /admin/usuarios, com todos os itens respondendo 403. O mesmo filtro
  // descarta um `moduloSelecionado` velho do localStorage que a org já não enxerga.
  const currentModule = getCurrentModule(location, moduloSelecionado, visibleMods.map(m => m.id));

  // Só rota DE MÓDULO atualiza a seleção. Rota de administração não mexe em nada —
  // é o que preserva o módulo de origem ao entrar em Conectores.
  useEffect(() => {
    if (PUBLIC_PATHS.includes(location) || !rotaDefineModulo(location)) return;
    setModuloSelecionado(currentModule.id);
    try {
      localStorage.setItem(LAST_MODULE_KEY, currentModule.id);
    } catch {
      // Sem localStorage a seleção só não sobrevive ao reload — não é motivo pra quebrar a tela.
    }
  }, [location, currentModule.id]);

  const handleNavigation = (path: string) => {
    setLocation(path);
    setOpenMobile(false);
  };

  const handleModuleSwitch = (mod: RizomaModule) => {
    setModuloSelecionado(mod.id); // reflete na hora; o effect confirma quando a rota troca
    handleNavigation(mod.homePath);
  };

  const [trocandoSenha, setTrocandoSenha] = useState(false);

  const handleLogout = () => {
    toast({ title: "Logout realizado", description: "Até logo!" });
    logout();
  };

  // Badges do Mercado (A receber / Ofertas). O hook só consulta quando a org é
  // produtora com o módulo — para as demais devolve zero sem request.
  const badgesMercado = useBadgesMercado();

  // Item ativo: rota exata, ou uma sub-rota dele quando o item já é um segundo
  // nível ("/mercado/corretores/:id" acende Corretores). A home de um módulo
  // ("/equipe", "/mercado") só acende na rota exata — senão acenderia sempre.
  const itemAtivo = (path: string) =>
    location === path || (path.split("/").length > 2 && location.indexOf(path + "/") === 0);

  // Itens de nav do módulo atual, filtrados por público (tipo de org, perfil,
  // feature desligada) e por PAPEL (`cap` de cada item). Tudo sai de
  // `itensDoPublico`, a mesma função da tabbar — as duas divergirem é como um
  // item desligado continua aparecendo em um dos dois menus. As abas por
  // usuário (`users.permissoes`/`permissaoId`) saíram no PR 4 (contract).
  const orgTipo = user?.orgTipo ?? "PRODUTOR";
  // O papel da sessão (em modo suporte, LEITURA — ver `usePode`).
  const papel = (user as { suporte?: unknown } | null)?.suporte ? "LEITURA" : user?.role ?? null;
  const moduleNavItems = itensDoPublico(currentModule.nav, {
    orgTipo,
    perfilComprador: user?.perfilComprador,
    role: papel,
  });

  // A seção Administração — quem vê o quê (módulo, papel, tipo de org e, desde o
  // lançamento gratuito, "Assinatura" só fora da CORTESIA) — mora em
  // `lib/menu-administracao.ts`, onde é testada sem montar a sidebar.
  const adminNavItems = itensDeAdministracao({
    modulesEnabled,
    orgTipo,
    papel,
    orgStatus: user?.orgStatus,
  });

  // As telas da PLATAFORMA (Cotações da praça, Praças, Pré-cadastro, Fazendas)
  // deixaram de ser um grupo solto aqui: são o nav do MÓDULO "plataforma", que
  // `visibleModules` acrescenta ao seletor só para quem tem
  // `users.plataforma_admin` (`isPlataforma`). O critério continua NÃO sendo
  // `isAdmin` — admin de fazenda administra a fazenda dele.

  return (
    <Sidebar>
      <SidebarHeader className="p-6 border-b">
        <div className="flex items-center gap-3 mb-3">
          <RizomaLogo className="h-10 w-10" />
          <div>
            <h1 className="text-xl font-bold">Rizoma Farm</h1>
          </div>
        </div>

        {/* Seletor de módulos — só aparece quando há 2+ módulos visíveis */}
        {visibleMods.length > 1 && (
          <div className="mb-3">
            <ModuleSwitcher
              current={currentModule}
              modules={visibleMods}
              onSelect={handleModuleSwitch}
              orgTipo={orgTipo}
            />
          </div>
        )}

        <div className="pt-2 border-t space-y-0.5">
          {user?.orgName && (
            <p className="text-xs font-medium text-foreground truncate" title={user.orgName}>
              {user.orgName}
            </p>
          )}
          <p className="text-xs text-muted-foreground truncate">
            {user?.name || (isAdmin ? "Administrador" : "Campo")}
          </p>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Navegação do módulo atual */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {moduleNavItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    asChild
                    isActive={itemAtivo(item.path)}
                    data-testid={`nav-${item.path.replace(/\//g, "-").slice(1) || "dashboard"}`}
                  >
                    <a
                      href={item.path}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavigation(item.path);
                      }}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{item.label}</span>
                    </a>
                  </SidebarMenuButton>
                  {item.badge && badgesMercado[item.badge] > 0 && (
                    <SidebarMenuBadge className="bg-atencao-suave text-atencao font-semibold" data-testid={`badge-${item.badge}`}>
                      {badgesMercado[item.badge]}
                    </SidebarMenuBadge>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Administração — persistente para ADMIN, independente de módulo.
            `length > 0` porque a org de COMPRADOR não tem item nenhum aqui, e um
            cabeçalho sozinho é pior que a seção não existir. */}
        {adminNavItems.length > 0 && currentModule.id !== "plataforma" && (
          <SidebarGroup>
            <SidebarGroupLabel>Administração</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminNavItems.map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      asChild
                      isActive={location === item.path}
                      data-testid={`nav-${item.path.slice(1)}`}
                    >
                      <a
                        href={item.path}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavigation(item.path);
                        }}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.label}</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

      </SidebarContent>

      <SidebarFooter className="p-4 border-t">
        <SidebarMenu>
          <SidebarMenuItem>
            {/* O que ESTA pessoa recebe pelo WhatsApp do Rizoma — qualquer papel. */}
            <SidebarMenuButton onClick={() => handleNavigation("/conta/avisos")} isActive={location === "/conta/avisos"} data-testid="button-meus-avisos">
              <Bell className="h-5 w-5" />
              <span>Meus avisos</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => setTrocandoSenha(true)} data-testid="button-trocar-senha">
              <KeyRound className="h-5 w-5" />
              <span>Trocar minha senha</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLogout} data-testid="button-logout">
              <LogOut className="h-5 w-5" />
              <span>Sair</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <TrocarSenhaDialog aberto={trocandoSenha} onFechar={() => setTrocandoSenha(false)} />
    </Sidebar>
  );
}
